import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Toaster } from '@/components/ui/sonner';
import { RequireAuth } from '@/components/require-auth';
import { RequireAdmin } from '@/components/require-admin';
import { RequireRole } from '@/components/require-role';
import { Layout } from '@/components/layout/layout';
import { Login } from '@/pages/login';
import { Obras } from '@/pages/obras';
import { ObraDetalle } from '@/pages/obra-detalle';
import { instalarRastreoModalidad } from '@/hooks/modalidad-foco';
// R4b (plan_migracion_reui.md, Ajuste 4): las pantallas solo para gestión /
// administración van en chunks propios (el data-grid, el date-selector y el
// NumberField no suman al bundle principal que baja todo el equipo).
const GestionClientes = lazy(() => import('@/pages/gestion-clientes').then(m => ({ default: m.GestionClientes })));
const Administracion = lazy(() => import('@/pages/administracion').then(m => ({ default: m.Administracion })));
const Auditoria = lazy(() => import('@/pages/auditoria').then(m => ({ default: m.Auditoria })));
// Avivador v2 (T5, plan_build_avivador_v2.md A4): `/avivador` monta el
// mosaico directo, en su propio chunk (no suma al bundle inicial si nadie lo
// abre). La v1 (Vidriera) queda archivada en context/antecedentes/avivador-v1/
// y en el tag de git `avivador-v1`.
const AvivadorMosaico = lazy(() => import('@/pages/avivador-mosaico').then(m => ({ default: m.AvivadorMosaico })));

// Rastreo de teclado/puntero para el anillo de foco al volver del Avivador
// (#8, §13 6.2): se instala a nivel de módulo (estático, se evalúa al
// arrancar la app) y no al montar el chunk lazy del mosaico, así un click en
// «Volver al Avivador» después de recargar /obras/:cc pasa aunque el chunk
// todavía no haya cargado (review de T3a, C5 + H6). Ver hooks/modalidad-foco.ts.
instalarRastreoModalidad();

// TooltipProvider (Base UI) envuelve todo: lo requiere el Tooltip usado en
// el rail colapsado del sidebar. Layout va como ruta pathless detrás de
// RequireAuth — mismo patrón que RISIT (ProtectedRoute > Layout > Outlet).
// Gestión es gestor+admin (RequireRole min="gestor"); Administración y
// Auditoría son admin-only (B.1 punto 4). El ítem de nav ya se oculta según
// el rol (nav-items.ts + sidebar.tsx); estos gates son la segunda capa, por
// si alguien entra a mano por URL.
export function App() {
  return (
    <TooltipProvider delay={300}>
      <Routes>
        <Route path="/login" element={<Login />} />

        <Route
          element={
            <RequireAuth>
              <Layout />
            </RequireAuth>
          }
        >
          <Route path="/" element={<Obras />} />
          <Route path="/obras/:cc" element={<ObraDetalle />} />
          {/* Avivador (design_avivador.md §2 punto 7): todos los roles lo
              ven — las acciones ya están gateadas por rol en el backend y en
              la UI del propio módulo (`useAvivadorEstado` deriva
              `puedeEscribir` de `me.rol`; gatea también Enter/P), no acá
              (sin RequireRole). `Suspense` por el `lazy` de AvivadorMosaico. */}
          <Route
            path="/avivador"
            element={
              <Suspense fallback={null}>
                <AvivadorMosaico />
              </Suspense>
            }
          />
          <Route
            path="/gestion"
            element={
              <RequireRole min="gestor">
                <Suspense fallback={null}>
                  <GestionClientes />
                </Suspense>
              </RequireRole>
            }
          />
          <Route
            path="/administracion"
            element={
              <RequireAdmin>
                <Suspense fallback={null}>
                  <Administracion />
                </Suspense>
              </RequireAdmin>
            }
          />
          <Route
            path="/auditoria"
            element={
              <RequireAdmin>
                <Suspense fallback={null}>
                  <Auditoria />
                </Suspense>
              </RequireAdmin>
            }
          />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Toaster />
    </TooltipProvider>
  );
}
